/**
 * Pure helpers for the webhook Delivery history dialog.
 *
 * Bodies come back from ClickHouse as raw text — usually JSON, sometimes an
 * HTML error page or plain "ok". The dialog wants to pretty-print JSON when it
 * can and fall back to the raw text otherwise, without ever throwing on a
 * truncated or malformed payload.
 */

export type ParsedDeliveryBody =
  | { kind: 'empty' }
  | { kind: 'json'; value: unknown }
  | { kind: 'text'; value: string };

/**
 * Classify a stored request/response body for display.
 *
 * - `null`, `undefined` or whitespace-only → `empty`
 * - valid JSON → `json` with the parsed value (object, array, string, number…)
 * - anything else (HTML, truncated JSON, plain text) → `text` with the raw string
 */
export function parseDeliveryBody(raw: string | null | undefined): ParsedDeliveryBody {
  if (raw === null || raw === undefined) return { kind: 'empty' };
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { kind: 'empty' };

  // Cheap pre-check so plain-text bodies like "ok" don't pay for a failed parse.
  const first = trimmed[0];
  if (first === '{' || first === '[' || first === '"') {
    try {
      return { kind: 'json', value: JSON.parse(trimmed) };
    } catch {
      return { kind: 'text', value: raw };
    }
  }
  return { kind: 'text', value: raw };
}

/**
 * Build the "Request payload" / "Response" section sub-title the dialog shows
 * next to a body that the API had to cut, so merchants know they are looking at
 * a prefix and not the full message.
 */
export function truncationNote(isTruncated: boolean, shippedChars: number): string | null {
  if (!isTruncated) return null;
  return `Showing the first ${shippedChars.toLocaleString('en-US')} characters`;
}
